import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import React from 'react';
import API from './api';
import { useAuth } from './auth';
import { playChime } from './sound';

const UnreadCtx = createContext({ counts: {}, total: 0, refresh: () => {}, isNew: () => false });

const POLL_MS = 8000;

export const UnreadProvider = ({ children }) => {
  const { user } = useAuth() || {};
  const [counts, setCounts] = useState({});
  const totalRef = useRef(0);
  const mountedRef = useRef(false);
  const mutedRef = useRef(localStorage.getItem('pk_muted') === '1');

  useEffect(() => {
    const onStorage = () => { mutedRef.current = localStorage.getItem('pk_muted') === '1'; };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const refresh = useCallback(async () => {
    if (!user || (user.role !== 'customer' && user.role !== 'partner')) {
      setCounts({}); totalRef.current = 0; return;
    }
    try {
      const r = await API.get('/orders/unread-counts');
      const data = r.data || {};
      const total = Object.values(data).reduce((a, b) => a + (b || 0), 0);
      // Play chime only on incoming increase (skip first mount to avoid startup chirp)
      if (mountedRef.current && total > totalRef.current && !mutedRef.current) {
        playChime(0.3);
      }
      mountedRef.current = true;
      totalRef.current = total;
      setCounts(data);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    mountedRef.current = false; // reset on user change
    refresh();
    const iv = setInterval(refresh, POLL_MS);
    return () => clearInterval(iv);
  }, [refresh]);

  const total = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
  const isNew = (oid) => (counts[oid] || 0) > 0;

  return (
    <UnreadCtx.Provider value={{ counts, total, refresh, isNew }}>
      {children}
    </UnreadCtx.Provider>
  );
};

export const useUnread = () => useContext(UnreadCtx);
